import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import Layout from "../components/common/Layout";
import SEO from "../components/common/SEO";
import StructuredData from "../components/common/StructuredData";
import FAQSchema from "../components/common/FAQSchema";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
};

const scaleIn = {
  hidden: { scale: 0.95, opacity: 0 },
  visible: { scale: 1, opacity: 1, transition: { duration: 0.5 } },
};

const HomePageRedesign: React.FC = () => {
  const { t } = useTranslation();

  // FAQ data
  const faqItems = [
    {
      question: "Is Nemlingo really like TikTok?",
      answer: "Yes! Just like TikTok, you get short, engaging video content designed to keep you hooked. Each lesson is a bite-sized video that teaches you real Danish in everyday situations - from ordering coffee to passing your citizenship test."
    },
    {
      question: "What can I learn with Nemlingo?",
      answer: "Everything you need for life in Denmark: PD2 & PD3 exam prep, citizenship test (medborgerskabsprøven), driving license theory, and daily conversations. We focus on what actually matters for your goals."
    },
    {
      question: "How is this different from Duolingo?",
      answer: "Duolingo teaches you generic phrases. Nemlingo teaches you Danish specifically for Denmark - the phrases, culture, and situations you'll actually encounter. Plus, our short video format makes learning feel like entertainment, not homework."
    },
    {
      question: "Can beginners use Nemlingo?",
      answer: "Absolutely! Start with basic daily conversations and work your way up to exam prep. Each video builds on the last, and you can practice speaking with instant feedback."
    },
    {
      question: "How long are the videos?",
      answer: "Most videos are 30-90 seconds - perfect for learning during your commute, coffee break, or whenever you have a spare moment. Just like scrolling TikTok, but you're actually learning Danish!"
    }
  ];

  // Target audiences
  const targets = [
    {
      emoji: "💬",
      title: "Daily Life",
      description: "Real conversations for everyday Denmark"
    },
    {
      emoji: "📚",
      title: "PD2 & PD3",
      description: "Exam preparation with real test scenarios"
    },
    {
      emoji: "🇩🇰",
      title: "Citizenship",
      description: "Medborgerskabsprøven & integration"
    },
    {
      emoji: "🚗",
      title: "Driving License",
      description: "Theory test in Danish"
    }
  ];

  // How it works
  const steps = [
    {
      number: "1",
      title: "Watch Short Videos",
      description: "Real Danish situations in bite-sized videos - just like TikTok"
    },
    {
      number: "2",
      title: "Practice Speaking",
      description: "Repeat what you hear and get instant feedback on your pronunciation"
    },
    {
      number: "3",
      title: "Take Fun Quizzes",
      description: "Test your knowledge with interactive questions"
    }
  ];

  return (
    <Layout>
      <SEO
        title="Nemlingo - TikTok for Learning Danish"
        description="Learn Danish for real life in Denmark. Short videos, fun quizzes, and real conversations. Perfect for PD2, PD3, citizenship, driving license, and daily life."
        keywords="learn danish, danish learning app, PD2, PD3, citizenship test, medborgerskabsprøven, driving license danish, learn danish online, danish for beginners"
      />
      <StructuredData
        type="WebSite"
        data={{
          name: "Nemlingo",
          url: "https://nemlingo.com",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://nemlingo.com/search?q={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <StructuredData
        type="Organization"
        data={{
          name: "Nemlingo",
          url: "https://nemlingo.com",
          logo: "https://nemlingo.com/logo512.png",
          description: "TikTok for Learning Danish. Short videos teaching real Danish for everyday life in Denmark - from PD2 exams to daily conversations.",
        }}
      />
      <FAQSchema faqs={faqItems} />

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 py-16 md:py-24">
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-12">
            <motion.div
              className="lg:w-1/2 text-center lg:text-left"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              <div className="inline-block mb-4 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full">
                <span className="text-white font-bold text-sm">🎬 TikTok for Learning Danish</span>
              </div>
              
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-black text-gray-900 leading-tight mb-6">
                Learn Danish for{" "}
                <span className="relative inline-block">
                  <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-transparent bg-clip-text">
                    real life
                  </span>
                  <span className="absolute bottom-0 left-0 w-full h-3 bg-yellow-300 -z-10 transform -rotate-1"></span>
                </span>
                {" "}in Denmark
              </h1>

              <p className="text-xl md:text-2xl text-gray-700 mb-4 font-semibold">
                PD2 · PD3 · Citizenship · Driving license · Daily conversations
              </p>

              <p className="text-lg text-gray-600 mb-8 max-w-xl">
                Short videos, real Danish, fun quizzes — built for everyday life.
              </p>

              <div className="flex flex-wrap gap-4 justify-center lg:justify-start mb-6">
                <a
                  href="https://apps.apple.com/dk/app/nemlingo/id6751247509"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                >
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    src="/6571906d42dadee6d16f0a74_apple_store.png"
                    alt="Download on the App Store"
                    className="w-[180px] h-[60px] object-contain"
                    loading="lazy"
                  />
                </a>

                <a
                  href="https://play.google.com/store/apps/details?id=com.nemlingo.app&pcampaignid=web_share"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                >
                  <motion.img
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.98 }}
                    src="/65719077d1409ad0b2795218_google_play.png"
                    alt="Get it on Google Play"
                    className="w-[180px] h-[60px] object-contain"
                    loading="lazy"
                  />
                </a>
              </div>

              <div className="flex items-center justify-center lg:justify-start gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-1">
                  <span className="text-yellow-500">⭐⭐⭐⭐⭐</span>
                  <span className="font-semibold">4.8</span>
                </div>
                <div className="font-semibold">10,000+ learners</div>
              </div>
            </motion.div>

            <motion.div
              className="lg:w-1/2"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-purple-400 rounded-3xl blur-3xl opacity-20"></div>
                
                {/* Marketing Visual - Video-style cards */}
                <div className="relative space-y-4">
                  {/* Card 1 - Top */}
                  <motion.div
                    className="bg-gradient-to-br from-blue-500 to-purple-600 rounded-3xl p-8 shadow-2xl transform rotate-2"
                    initial={{ x: 50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl">
                        🎬
                      </div>
                      <div className="text-white">
                        <div className="font-bold text-lg">Short Videos</div>
                        <div className="text-sm opacity-90">30-90 seconds each</div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-white">
                      <div className="text-2xl font-black mb-2">Hvordan har du det?</div>
                      <div className="text-sm opacity-90">How are you? 🇩🇰</div>
                    </div>
                  </motion.div>

                  {/* Card 2 - Middle */}
                  <motion.div
                    className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-3xl p-8 shadow-2xl transform -rotate-1"
                    initial={{ x: -50, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.5 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl">
                        🎯
                      </div>
                      <div className="text-white">
                        <div className="font-bold text-lg">Real Situations</div>
                        <div className="text-sm opacity-90">Everyday Denmark</div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-white">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xl">📚</span>
                        <span className="font-bold">PD2 Exam</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xl">🇩🇰</span>
                        <span className="font-bold">Citizenship Test</span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Card 3 - Bottom */}
                  <motion.div
                    className="bg-gradient-to-br from-orange-500 to-red-600 rounded-3xl p-8 shadow-2xl transform rotate-1"
                    initial={{ y: 50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.7 }}
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center text-2xl">
                        🎤
                      </div>
                      <div className="text-white">
                        <div className="font-bold text-lg">Practice Speaking</div>
                        <div className="text-sm opacity-90">Instant feedback</div>
                      </div>
                    </div>
                    <div className="bg-white/20 backdrop-blur-sm rounded-2xl p-4 text-white">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold">Pronunciation</span>
                        <span className="text-2xl">✓</span>
                      </div>
                      <div className="w-full bg-white/30 rounded-full h-2">
                        <div className="bg-white rounded-full h-2 w-4/5"></div>
                      </div>
                    </div>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Targeted Learning Section */}
      <section className="py-20 bg-gradient-to-br from-green-50 to-white" id="targets">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Danish{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-green-600 to-emerald-600 text-transparent bg-clip-text">
                  tailored
                </span>
                <span className="absolute bottom-0 left-0 w-full h-3 bg-green-300 -z-10"></span>
              </span>
              {" "}to your goals
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Choose what you need — we focus your learning.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {targets.map((target, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
              >
                <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all border-2 border-gray-100 h-full text-center">
                  <div className="text-5xl mb-4">{target.emoji}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    {target.title}
                  </h3>
                  <p className="text-gray-600 text-sm">{target.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-white" id="how-it-works">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-16"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Learn Danish with{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-yellow-600 to-orange-600 text-transparent bg-clip-text">
                  short videos
                </span>
                <span className="absolute bottom-0 left-0 w-full h-3 bg-yellow-300 -z-10"></span>
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Watch real Danish situations — then practice with fun quizzes.
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Listening · Speaking · Vocabulary
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {steps.map((step, index) => (
              <motion.div
                key={index}
                variants={scaleIn}
                whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
              >
                <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 h-full text-center border-2 border-blue-100">
                  <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center mb-6 text-white text-3xl font-black mx-auto shadow-lg">
                    {step.number}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">
                    {step.title}
                  </h3>
                  <p className="text-gray-600">{step.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Confidence/Outcome Section */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">
              Speak English with{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-orange-600 to-red-600 text-transparent bg-clip-text">
                  confidence
                </span>
                <span className="absolute bottom-0 left-0 w-full h-3 bg-orange-300 -z-10"></span>
              </span>
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto mb-8">
              From the classroom to real conversations — faster and easier.
            </p>
          </motion.div>

          <motion.div
            className="max-w-4xl mx-auto"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl">
              <div className="flex flex-col md:flex-row items-center justify-between gap-8 mb-8">
                <div className="text-center md:text-left">
                  <div className="text-5xl mb-2">🎓</div>
                  <div className="font-bold text-gray-900">Beginner</div>
                </div>
                <div className="flex-1 relative flex items-center">
                  <div className="w-full h-3 bg-gradient-to-r from-orange-400 to-red-500 rounded-full"></div>
                  <div className="absolute -right-3 w-0 h-0 border-t-[12px] border-t-transparent border-b-[12px] border-b-transparent border-l-[20px] border-l-red-500"></div>
                </div>
                <div className="text-center md:text-right">
                  <div className="text-5xl mb-2">💬</div>
                  <div className="font-bold text-gray-900">Confident</div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-2xl p-6 border-2 border-orange-200">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="text-yellow-500 text-xl">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-700 italic text-center mb-2">
                    "I passed my PD3 exam thanks to Nemlingo!"
                  </p>
                  <p className="text-gray-600 text-sm text-center">— Maria, Copenhagen</p>
                </div>

                <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border-2 border-blue-200">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="text-yellow-500 text-xl">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-700 italic text-center mb-2">
                    "Finally got my citizenship! The short videos made learning so easy."
                  </p>
                  <p className="text-gray-600 text-sm text-center">— Ahmed, Aarhus</p>
                </div>

                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-2xl p-6 border-2 border-green-200">
                  <div className="flex items-center justify-center gap-2 mb-3">
                    <span className="text-yellow-500 text-xl">⭐⭐⭐⭐⭐</span>
                  </div>
                  <p className="text-gray-700 italic text-center mb-2">
                    "Better than Duolingo! I can actually use Danish in my daily life now."
                  </p>
                  <p className="text-gray-600 text-sm text-center">— Sarah, Odense</p>
                </div>
              </div>
            </div>
          </motion.div>

          <p className="text-center text-gray-600 mt-8 text-sm">
            Made for everyday Danish life in Denmark.
          </p>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white relative overflow-hidden" id="cta">
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
          <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-white opacity-10 rounded-full blur-3xl"></div>
        </div>
        <div className="container mx-auto px-4 text-center max-w-4xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <h2 className="text-4xl md:text-5xl font-black mb-6">
              Start learning Danish today
            </h2>
            <p className="text-xl md:text-2xl mb-10 opacity-95">
              Join 10,000+ learners mastering Danish for real life in Denmark
            </p>
            
            <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
              <a
                href="https://apps.apple.com/dk/app/nemlingo/id6751247509"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download on the App Store"
              >
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  src="/6571906d42dadee6d16f0a74_apple_store.png"
                  alt="Download on the App Store"
                  className="w-[180px] h-[60px] object-contain mx-auto"
                  loading="lazy"
                />
              </a>

              <a
                href="https://play.google.com/store/apps/details?id=com.nemlingo.app&pcampaignid=web_share"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Get it on Google Play"
              >
                <motion.img
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                  src="/65719077d1409ad0b2795218_google_play.png"
                  alt="Get it on Google Play"
                  className="w-[180px] h-[60px] object-contain mx-auto"
                  loading="lazy"
                />
              </a>
            </div>
            
            <div className="flex flex-wrap justify-center gap-6 text-sm opacity-90">
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span>Free to start</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span>Short video lessons</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span>Real Danish for Denmark</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePageRedesign;
